import React, { useState, useEffect } from 'react';
import { User, Delete } from 'lucide-react';
import { styles } from '../theme';
import { API_URL, fetchWithAuth } from '../api';

/**
 * Tap-your-tile, then PIN — not a blind global PIN box. Loyverse-style
 * operator switch: identifies who's running a shared POS terminal.
 * Renders a keypad and calls onVerified(operator, operatorToken) on success.
 */
const PosPinPad = ({ token, branchId, onVerified }) => {
  const [operators, setOperators] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = branchId ? `?branch_id=${branchId}` : '';
    fetchWithAuth(`${API_URL}/pos/operators${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setOperators(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching operators:', err));
  }, [token, branchId]);

  const pressDigit = (d) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    setError('');
    if (next.length === 4) {
      submit(next);
    }
  };

  const submit = async (fullPin) => {
    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: selected.id, pin: fullPin })
      });
      const data = await res.json();
      if (res.ok) {
        onVerified(data.operator, data.operator_token);
      } else {
        setError(data.error || 'Incorrect PIN');
        setPin('');
      }
    } catch (err) {
      setError('Network error, please try again');
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  if (!selected) {
    return (
      <div style={{ maxWidth: '480px', margin: '48px auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>Who's operating?</h2>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>Tap your name to enter your PIN</p>
        {operators.length === 0 ? (
          <div style={{ ...styles.card, color: '#6b7280', padding: '32px' }}>
            No staff are enrolled with a POS PIN yet. Set one up from the Users screen.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
            {operators.map(op => (
              <button
                key={op.id}
                onClick={() => { setSelected(op); setError(''); setPin(''); }}
                style={{
                  ...styles.card, cursor: 'pointer', border: '1px solid #e5e7eb',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                  padding: '20px 12px', minHeight: '88px'
                }}
              >
                <User size={28} style={{ color: '#4f46e5' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>{op.fullname || op.username}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '320px', margin: '48px auto', textAlign: 'center' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>{selected.fullname || selected.username}</h2>
      <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>Enter your 4-digit PIN</p>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            style={{
              width: '18px', height: '18px', borderRadius: '50%',
              border: '2px solid #4f46e5',
              backgroundColor: pin.length > i ? '#4f46e5' : 'transparent'
            }}
          />
        ))}
      </div>

      {error && <p style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
          <button
            key={d}
            disabled={submitting}
            onClick={() => pressDigit(d)}
            style={{
              padding: '18px 0', fontSize: '20px', fontWeight: 600, borderRadius: '8px',
              border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer'
            }}
          >
            {d}
          </button>
        ))}
        <button
          onClick={() => { setSelected(null); setPin(''); setError(''); }}
          style={{ padding: '18px 0', fontSize: '13px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer' }}
        >
          Back
        </button>
        <button
          disabled={submitting}
          onClick={() => pressDigit('0')}
          style={{ padding: '18px 0', fontSize: '20px', fontWeight: 600, borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer' }}
        >
          0
        </button>
        <button
          onClick={() => { setPin(pin.slice(0, -1)); setError(''); }}
          style={{ padding: '18px 0', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Delete size={18} />
        </button>
      </div>
    </div>
  );
};

export default PosPinPad;
